import { Inbox, PlusCircle } from "lucide-react"
import { useId } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "@/lib/page-rendering"

import { Button } from "@/components/ui/button"
import { Card, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import type SearchShortlistsShortlistList from "@/types/serializers/SearchShortlistsShortlistList"

export function ShortlistCard({
  shortlist,
  children,
}: {
  shortlist: SearchShortlistsShortlistList
  children: React.ReactNode
}) {
  const labelId = useId()
  const { t } = useTranslation("shortlists")

  return (
    <Card role="listitem" aria-labelledby={labelId}>
      <CardHeader>
        <CardTitle id={labelId}>{shortlist.name}</CardTitle>
        <p className="text-muted-foreground text-sm">
          {t("index.class_count", { count: shortlist.class_count })}
          {" · "}
          {t("index.educator_count", { count: shortlist.educator_count })}
        </p>
      </CardHeader>
      <CardFooter className="flex flex-row flex-wrap gap-4">
        {children}
      </CardFooter>
    </Card>
  )
}

export function ShortlistList({
  shortlists,
  canCreate,
  ActionsComponent,
}: {
  shortlists: Array<SearchShortlistsShortlistList>
  canCreate: boolean
  ActionsComponent: React.ComponentType<{
    shortlist: SearchShortlistsShortlistList
  }>
}) {
  const { t } = useTranslation("shortlists")

  if (shortlists.length === 0) {
    return <ShortlistListEmpty canCreate={canCreate} />
  }

  return (
    <ul
      className="grid auto-rows-fr gap-5"
      aria-label={t("index.title")}
      data-testid="shortlist-list"
    >
      {shortlists.map((shortlist) => (
        <ShortlistCard key={shortlist.id} shortlist={shortlist}>
          <ActionsComponent shortlist={shortlist} />
        </ShortlistCard>
      ))}
    </ul>
  )
}

function ShortlistListEmpty({ canCreate }: { canCreate: boolean }) {
  const { t } = useTranslation("shortlists")

  return (
    <Empty data-testid="shortlist-list-empty">
      <EmptyHeader>
        <EmptyMedia variant="icon" className="text-muted size-20 bg-white">
          <Inbox className="size-16" />
        </EmptyMedia>
        <EmptyTitle data-testid="empty-title">
          {t("index.empty_list_title")}
        </EmptyTitle>
        <EmptyDescription>
          {canCreate
            ? t("index.empty_list_description")
            : t("index.empty_list_unavailable_description")}
        </EmptyDescription>
      </EmptyHeader>
      {canCreate && (
        <EmptyContent>
          <Button asChild>
            <Link href="/search/shortlists/new">
              <PlusCircle />
              {t("index.create")}
            </Link>
          </Button>
        </EmptyContent>
      )}
    </Empty>
  )
}
